import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Group } from '../components/header/group-enum';

/** Providing account releated data and functions */

const LOGIN_URL = environment.api + "api/login";

const COMPONENT_VIEW_ROLES: Group[] = [
  Group.Global_Admin, Group.Global_Viewer, Group.Bike_Admin, Group.Bike_Viewer, Group.Software_Admin, Group.Software_Viewer,
  Group.Configuration_Admin, Group.Configuration_Viewer, Group.Component_Admin, Group.Component_Viewer
];
const COMPONENT_EDIT_ROLES: Group[] = [Group.Global_Admin, Group.Component_Admin, Group.Bike_Admin];
const COMPONENT_SUPPORT_ROLES: Group[] = [
  Group.Global_Admin, Group.Global_Viewer, Group.Bike_Admin, Group.Bike_Viewer, Group.Software_Admin, Group.Software_Viewer,
  Group.Configuration_Admin, Group.Configuration_Viewer, Group.Component_Admin, Group.Component_Viewer, Group.Component_Support
]
const COMPONENT_SET_RECALLED_FLAG_ROLES: Group[] = [
  Group.Global_Admin, Group.Bike_Admin, Group.Software_Admin, Group.Configuration_Admin, Group.Component_Admin
]

const CONFIGURATION_VIEW_ROLES: Group[] = [
  Group.Global_Admin, Group.Global_Viewer, Group.Configuration_Admin, Group.Bike_Admin, Group.Bike_Support, Group.Bike_Viewer,
  Group.Software_Admin, Group.Software_Viewer, Group.Configuration_Admin, Group.Configuration_Viewer
];
const CONFIGURATION_MODIFY_ROLES: Group[] = [
  Group.Global_Admin, Group.Bike_Admin, Group.Software_Admin, Group.Configuration_Admin
];

const SOFTWARE_VIEW_ROLES: Group[] = [Group.Global_Admin, Group.Global_Viewer, Group.Software_Admin, Group.Software_Viewer];
const SOFTWARE_MODIFY_ROLES: Group[] = [Group.Global_Admin, Group.Software_Admin];

const BIKE_VIEW_ROLES: Group[] = [Group.Global_Admin, Group.Global_Viewer, Group.Bike_Viewer, Group.Bike_Admin, Group.Software_Admin, Group.Software_Viewer];
const UNASSIGNED_BIKES_VIEW_ROLES: Group[] = [Group.Global_Admin, Group.Global_Viewer, Group.Bike_Viewer, Group.Bike_Admin];
const BIKE_MODIFY_ROLES: Group[] = [Group.Global_Admin, Group.Bike_Admin];
const BIKE_UNMAP_ROLES: Group[] = [Group.Global_Admin, Group.Bike_Support];

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  public User: { Name: string, Email: string, Groups: Group[], OEMs: number[] } =
    {
      Name: "Unkown", Email: "", Groups: [], OEMs: []
    };

  constructor(private http: HttpClient, private cookieService: CookieService,
    private authService: MsalService) {
  }

  public async loadUser() {
    await this.authService.instance.handleRedirectPromise();
    this.User.Name = this.authService.instance.getAllAccounts()[0].name || "";
    this.User.Email = this.authService.instance.getAllAccounts()[0].username;
  }

  /** Calling login Api endpoint */
  public login(postData: { client_id: string, client_secret: string }): Observable<any> {
    return this.http.post(LOGIN_URL, postData);
  }

  /** Calls azure logout */
  public logout(): void {
    this.authService.logout();
  }

  public setGroup(group: Group[]) {
    this.User.Groups = group;
  }

  public getGroup(): Group[] {
    return this.User.Groups;
  }

  public setOEMs(oems: number[]) {
    this.User.OEMs = oems;
  }

  public getOEMs(): number[] {
    return this.User.OEMs;
  }

  public hasValidOEM(): boolean {
    return !(this.User.OEMs.length == 1 && this.User.OEMs[0] == 0);
  }

  public hasComponentViewRole(): boolean {
    return COMPONENT_VIEW_ROLES.some(group => this.User.Groups.includes(group));
  }

  public hasComponentEditRole(): boolean{
    return COMPONENT_EDIT_ROLES.some(group => this.User.Groups.includes(group));
  }

  public hasComponentSupportRole(): boolean {
    return COMPONENT_SUPPORT_ROLES.some(group => this.User.Groups.includes(group));
  }

  public hasComponentSetRecalledFlagRole(): boolean {
    return COMPONENT_SET_RECALLED_FLAG_ROLES.some(group => this.User.Groups.includes(group));
  }

  public hasConfigurationViewRole(): boolean {
    return CONFIGURATION_VIEW_ROLES.some(group => this.User.Groups.includes(group));
  }

  public hasConfigurationModifyRole(): boolean {
    return CONFIGURATION_MODIFY_ROLES.some(group => this.User.Groups.includes(group));
  }

  public hasSoftwareViewRole(): boolean {
    return SOFTWARE_VIEW_ROLES.some(group => this.User.Groups.includes(group));
  }

  public hasSoftwareModifyRole(): boolean {
    return SOFTWARE_MODIFY_ROLES.some(group => this.User.Groups.includes(group));
  }

  public hasBikeViewRole(): boolean {
    return BIKE_VIEW_ROLES.some(group => this.User.Groups.includes(group));
  }
  
  public hasUnassignedBikesViewRole(): boolean{
    return UNASSIGNED_BIKES_VIEW_ROLES.some(group => this.User.Groups.includes(group));
  }

  public hasBikeModifyRole(): boolean {
    return BIKE_MODIFY_ROLES.some(role => this.User.Groups.includes(role));
  }

  public hasBikeUnmapRole(): boolean {
    return BIKE_UNMAP_ROLES.some(role => this.User.Groups.includes(role));
  }
}